import { BIFROST_WHITE_PAPER } from "../nav/navigation";
import type { CtaLink } from "./types";

/**
 * The Bifrost section of odingard.com: seven short pages instead of one long
 * scroll. Every capability named here maps to an implementation path and test
 * in Odingard/Bifrost (see docs/bifrost-claims-matrix.md). Present tense means
 * built; nothing on these pages is a roadmap item.
 */

export const BIFROST_SITE = "https://bifrost.odingard.com";
export const BIFROST_VERIFY_REPO = "https://github.com/Odingard/bifrost-verify";
export const BIFROST_VERIFY_NPM = "https://www.npmjs.com/package/@odingard/bifrost-verify";
export const BIFROST_VERIFY_SPEC = `${BIFROST_VERIFY_REPO}/blob/main/SPEC.md`;

export const BIFROST_CONTACT = {
  demo: "/contact?topic=bifrost-demo",
  partner: "/contact?topic=bifrost-partner",
  integration: "/contact?topic=bifrost-integration",
} as const;

export type BifrostPageKey =
  | "overview"
  | "platform"
  | "agent-authorization"
  | "industries"
  | "security-reviews"
  | "verify"
  | "architecture";

export type BifrostPageDef = {
  key: BifrostPageKey;
  path: string;
  navLabel: string;
  title: string;
  lede: string;
};

export const BIFROST_PAGES: BifrostPageDef[] = [
  {
    key: "overview",
    path: "/bifrost",
    navLabel: "Overview",
    title: "Bifrost — The Autonomous Trust Center",
    lede: "The trust layer for autonomous organizations: organizational identity, delegated authority, governed exchange, signed evidence and independent verification.",
  },
  {
    key: "platform",
    path: "/bifrost/platform",
    navLabel: "Platform",
    title: "Bifrost Platform — One infrastructure, every governed exchange",
    lede: "Organizational trust, delegated authority, policy enforcement, evidence intelligence, verifiable records and interoperability in a single architecture.",
  },
  {
    key: "agent-authorization",
    path: "/bifrost/agent-authorization",
    navLabel: "Agent authorization",
    title: "Bifrost — Cross-organization agent authorization",
    lede: "Delegated authority, signed action proposals, counterparty policy decisions and a verifiable action chain across organizational boundaries.",
  },
  {
    key: "industries",
    path: "/bifrost/industries",
    navLabel: "Industries",
    title: "Bifrost — Industry-agnostic trust infrastructure",
    lede: "One trust architecture. Different business evidence. The same verification principles.",
  },
  {
    key: "security-reviews",
    path: "/bifrost/security-reviews",
    navLabel: "Security reviews",
    title: "Bifrost — Enterprise security reviews",
    lede: "Enterprise security reviews: governed, automated and verifiable, for the vendor that provides evidence and the buyer that verifies it.",
  },
  {
    key: "verify",
    path: "/bifrost/verify",
    navLabel: "Verification",
    title: "Bifrost — Independent verification",
    lede: "Signed records counterparties can examine independently, with the open-source Bifrost verifier.",
  },
  {
    key: "architecture",
    path: "/bifrost/architecture",
    navLabel: "Architecture",
    title: "Bifrost — Trust architecture for enterprise systems",
    lede: "OAuth 2.1, DPoP, MCP, A2A v1.0, OpenID AuthZEN, Cedar, signed receipts, tenant isolation, delegated authority, revocation and action-chain evidence.",
  },
];

export const bifrostPage = (path: string) =>
  BIFROST_PAGES.find((p) => p.path === path);

export const BIFROST_HERO = {
  eyebrow: "Bifrost | The Autonomous Trust Center",
  h1: "The Trust Layer for Autonomous Organizations.",
  supporting:
    "Establish identity. Enforce authority. Govern every exchange. Verify the outcome.",
  body: [
    "Autonomous systems are transforming how organizations exchange information, make decisions, and conduct business.",
    "Bifrost provides the trust infrastructure behind those interactions—connecting organizational identity, delegated authority, policy-controlled disclosure, evidence-backed decisions, and independently verifiable records.",
    "One trust architecture. Across organizational boundaries. Across industries.",
  ],
  ctas: [
    { label: "Start free", href: BIFROST_SITE, external: true },
    { label: "See pricing", href: `${BIFROST_SITE}/#pricing`, external: true },
    { label: "Request a Demo", href: BIFROST_CONTACT.demo },
  ] satisfies CtaLink[],
  pillars: [
    "Organizational identity",
    "Delegated authority",
    "Governed exchange",
    "Signed evidence",
    "Independent verification",
  ],
};

export const BIFROST_PROBLEM = {
  heading: "AI agents can communicate. Communication is not trust.",
  body: [
    "An authenticated connection does not establish what an agent is authorized to do.",
    "A generated response does not establish that its evidence was approved for disclosure.",
    "A digitally signed message does not, by itself, establish that a business action was permitted.",
    "Organizations need a verifiable relationship between identity, delegated authority, policy decisions, and resulting records. Bifrost provides that relationship.",
  ],
  questions: [
    { q: "Who is acting?", a: "Establish the organization, agent, and accountable authority." },
    { q: "What is authorized?", a: "Evaluate delegated permissions, limits, purpose, and expiry." },
    { q: "What can be exchanged?", a: "Apply the evidence owner’s disclosure and authorization policies." },
    { q: "What can be verified?", a: "Examine the signed records, supporting evidence references, and relevant authorization claims." },
  ],
  closing: "Trust is not an assumption. It is a decision supported by verifiable evidence.",
};

export const BIFROST_WHY = {
  heading: "More than automation. Verifiable organizational trust.",
  items: [
    { title: "Evidence owners retain control.", body: "The disclosing organization determines what information is authorized for release." },
    { title: "Agent authority is explicit.", body: "Permissions are scoped, bounded, attributable, and revocable." },
    { title: "Counterparties verify independently.", body: "Signatures, relevant key information, evidence references, and authorization records can be examined without relying solely on a vendor assertion." },
    { title: "The trust model extends across use cases.", body: "The same core architecture supports governed organizational exchanges independent of any single questionnaire format or industry." },
  ],
  closing:
    "Bifrost is not another AI answer generator. It is the trust infrastructure governing what autonomous systems are authorized to exchange and what counterparties can verify.",
};

export const BIFROST_CAPABILITIES = {
  heading: "One infrastructure. Every governed exchange.",
  body: [
    "Bifrost combines organizational trust, policy-based authorization, evidence governance, and cryptographic verification in a single architecture.",
    "The same core trust mechanisms apply whether the exchange concerns a security assessment, supplier qualification, business approval, audit evidence, or an autonomous agent request.",
  ],
  items: [
    {
      title: "Organizational Trust",
      points: [
        "Organizational identity and trust domains",
        "Agent authentication",
        "Identity assurance: self-asserted, domain-verified, IdP-verified",
        "Accountable principals",
        "Signing keys and verification, including customer-held roots",
      ],
    },
    {
      title: "Delegated Authority",
      points: [
        "Scoped agent permissions",
        "Delegation chains, up to three levels, each narrowing its parent",
        "Purpose and parameter limits",
        "Expiration and revocation",
        "Counterparty authorization",
      ],
    },
    {
      title: "Policy Enforcement",
      points: [
        "Cedar authorization decisions",
        "Governed access, applied before retrieval",
        "Evidence disclosure controls by tier and agreement",
        "Refusal and escalation to a person",
        "Tenant isolation and a per-tenant kill switch",
      ],
    },
    {
      title: "Evidence Intelligence",
      points: [
        "Evidence management and connector imports",
        "Version and freshness controls",
        "Evidence-backed answers with citations",
        "Citation checking against the exact excerpt",
        "Source-conflict detection that refuses contradictory answers",
      ],
    },
    {
      title: "Verifiable Records",
      points: [
        "Signed answer receipts",
        "Signed review packets",
        "Signed action decisions",
        "Acceptance and execution-report records",
        "Independent verification against an append-only transparency log",
      ],
    },
    {
      title: "Interoperability",
      points: [
        "MCP",
        "A2A v1.0",
        "OAuth 2.1 and DPoP",
        "OpenID AuthZEN authorization decision interface",
        "W3C verifiable credentials and an open-source verifier",
      ],
    },
  ],
};

export type FlowStep = { key: string; title: string; body: string };

export const BIFROST_FLOW = {
  heading: "From organizational request to verifiable decision.",
  body: "Both organizations retain their own identity, evidence boundaries, policy authority, and signing controls. Bifrost governs the exchange between them; neither side hands its infrastructure to the other.",
  steps: [
    { key: "identify", title: "Identify", body: "Authenticate the principal and establish the relevant organizational identity." },
    { key: "delegate", title: "Delegate", body: "Verify the agent’s delegated authority, purpose, scope, and limitations." },
    { key: "authorize", title: "Authorize", body: "Evaluate the exact request against applicable access and counterparty policies." },
    { key: "exchange", title: "Exchange", body: "Release authorized evidence-backed information or issue the applicable signed action decision." },
    { key: "attest", title: "Attest", body: "Generate signed artifacts recording the action, decision, supporting context, and subsequent acknowledgment or reported execution." },
    { key: "verify", title: "Verify", body: "Allow the receiving organization to independently examine the cryptographic and authorization claims." },
  ] satisfies FlowStep[],
  refusal:
    "When authorization or the required evidence is unavailable, Bifrost refuses and records why. A refusal is not a failed operation. It is part of the trust model.",
};

export const BIFROST_AGENT_AUTH = {
  heading: "Autonomous agents need accountable authority.",
  body: [
    "Bifrost enables organizations to govern agent-initiated requests across organizational boundaries.",
    "An agent can present its delegated authority, submit a signed action proposal, and receive an authorization decision governed by the receiving organization’s policies.",
    "Bifrost records the decision and the corresponding evidence of the exchange.",
  ],
  statement:
    "Authentication establishes identity. Delegation establishes authority. Policy determines permission. Cryptographic records preserve accountability.",
  boundary:
    "Bifrost governs and records the trust exchange. The organizations’ own business systems perform the underlying operation.",
};

export const BIFROST_INDUSTRIES = {
  heading: "Trust is not industry-specific.",
  supporting: "One trust architecture. Different business evidence. The same verification principles.",
  body: [
    "Every industry faces the same fundamental trust questions: Who is requesting information? What authority do they possess? Which evidence supports the response? Which actions are permitted? How can another organization verify what occurred?",
    "Bifrost addresses these questions through a common trust infrastructure.",
    "Industry-specific evidence formats, policy requirements, and integrations connect to the underlying architecture without redefining its trust model.",
  ],
  items: [
    { name: "Cybersecurity & Vendor Trust", body: "Evidence-backed security reviews, questionnaires, governed disclosures, signed packets, and buyer verification." },
    { name: "Procurement & Supplier Trust", body: "Governed supplier information exchange, authorization of supplier-related requests, and verifiable qualification records." },
    { name: "Finance & Business Assurance", body: "Controlled exchange and verification of financial or organizational evidence." },
    { name: "Healthcare Administration", body: "Policy-controlled administrative and organizational evidence exchange, subject to applicable deployment and privacy controls." },
    { name: "Legal & Due Diligence", body: "Controlled disclosures, evidence provenance, and verifiable review records." },
    { name: "Supply Chain & Manufacturing", body: "Supplier evidence, certification records, and verifiable organizational exchanges." },
    { name: "Enterprise AI Governance", body: "Agent identity, bounded delegation, authorization decisions, and signed action records." },
    { name: "Government & Regulated Operations", body: "Controlled exchanges governed by organization-specific authorization, evidence, residency, and security requirements." },
  ],
  note:
    "These are applications of one trust architecture, not eight separately certified or pre-integrated products. Industry evidence formats and integrations are scoped with each customer during onboarding.",
};

export const BIFROST_SECURITY_REVIEWS = {
  heading: "Enterprise security reviews. Governed, automated, and verifiable.",
  body: [
    "Bifrost turns approved organizational evidence into reusable security-review answers and independently verifiable records.",
    "Vendors control disclosure. Authorized buyers and agents request evidence. Answers are supported by citations, reviewed when required, and captured in signed receipts and review packets.",
  ],
  steps: [
    { title: "Publish approved evidence", body: "Upload policies and reports, or import them from Vanta, Drata or Google Drive. Nothing is published until an owner confirms it and sets its tier." },
    { title: "Set disclosure and access policies", body: "Tiers, NDA agreements and purposes decide what each reviewer and agent may see. Cedar policy runs before anything is retrieved." },
    { title: "Receive buyer or agent requests", body: "Reviewers ask in the trust portal. Their agents connect over MCP or A2A with OAuth 2.1, and restricted material is never released without a DPoP-bound token." },
    { title: "Produce supported answers or route exceptions", body: "Answers cite the exact evidence. Unsupported or conflicting questions are refused and routed to a person. SIG Lite and CAIQ v4 questionnaires are answered with confidence routing and owner approval." },
    { title: "Generate signed review artifacts", body: "Every released answer gets a signed receipt, and a closed review produces a signed packet, both appended to the transparency log." },
    { title: "Enable independent buyer verification", body: "Buyers verify receipts and packets with the open-source verifier, and keep packets from many vendors in one buyer workspace." },
    { title: "Track review outcomes and evidence changes", body: "Packet acceptance is recorded when the buyer’s agent retrieves it, and buyers are notified when evidence behind their answers is superseded, unpublished or expires." },
  ],
  sides: [
    { title: "For the vendor providing evidence", body: "A governed trust center: evidence library, disclosure policy, questionnaires, escalations and signed answers, without sending raw documents to every buyer." },
    { title: "For the organization receiving it", body: "Cited answers, a signed review packet, offline verification, and alerts when the evidence behind a past answer changes." },
  ],
  cta: { label: "See the Autonomous Trust Center", href: BIFROST_CONTACT.demo } satisfies CtaLink,
};

export const BIFROST_VERIFY = {
  heading: "Trust that does not require taking our word for it.",
  body: [
    "Bifrost produces cryptographically signed records that counterparties can examine independently.",
    "Verification distinguishes record integrity, key status, delegated authority, authorization decisions, and reported execution where applicable.",
    "A signed record provides verifiable provenance. The underlying evidence and applicable policies determine what can legitimately be concluded from it.",
  ],
  cta: { label: "Explore Independent Verification", href: BIFROST_VERIFY_REPO, external: true } satisfies CtaLink,
  install: "npm install -g @odingard/bifrost-verify",
  version: "0.1.1",
};

export const BIFROST_ARCHITECTURE = {
  heading: "Built for enterprise systems. Designed for autonomous operations.",
  body: "Bifrost implements the interoperability contracts and security controls enterprise systems already rely on. Cedar makes every authorization decision; the AuthZEN interface exposes those decisions to your identity provider or gateway, which remains the enforcement point.",
  items: [
    { name: "OAuth 2.1", body: "Authorization-code flow with PKCE for agents and reviewers, per-tenant protected-resource metadata." },
    { name: "DPoP", body: "Sender-constrained tokens (RFC 9449). Restricted material is never released without a DPoP-bound token, and owners can require DPoP workspace-wide." },
    { name: "MCP", body: "Agents connect to a tenant trust center over the Model Context Protocol to ask, search and browse governed evidence." },
    { name: "A2A v1.0", body: "A tenant Agent Card and a JSON-RPC SendMessage endpoint, sharing MCP’s token, policy, citation and receipt guarantees." },
    { name: "OpenID AuthZEN PDP", body: "Access-evaluation endpoints (single and batch) backed by the real Cedar decision. A decision point, never an enforcement point." },
    { name: "Cedar authorization", body: "The owner’s policy decides what each principal may see or do for a purpose, before retrieval and before any action decision." },
    { name: "Signed receipts", body: "Ed25519 JWS receipts bound to a Merkle root of the cited evidence, logged in an RFC 6962 transparency log with witness cosignatures and OpenTimestamps anchors." },
    { name: "Independent verification", body: "The Apache-2.0 @odingard/bifrost-verify package and CLI verify receipts, packets, credentials and action chains offline." },
    { name: "Tenant isolation", body: "Row-level security forced on every tenant table, envelope-encrypted secrets, rate limits and a per-tenant kill switch for agent access." },
    { name: "Delegated authority", body: "Recorded delegation chains up to three levels; each child can only narrow its parent’s actions, limits, purpose and expiry." },
    { name: "Revocation", body: "Revoking a delegation blocks every later decision that depends on it. Earlier records stay verifiable and show when revocation happened." },
    { name: "Action-chain evidence", body: "Proposal, decision, acceptance and execution report are linked by hash and exported as a single chain the verifier checks offline." },
  ],
  boundary:
    "Bifrost does not execute purchases, move money or operate a customer’s ERP. The customer’s business systems perform the underlying operations. Bifrost governs and records the trust exchange.",
  docs: [
    { kind: "Specification", label: "Bifrost exchange spec (receipts, packets, credentials)", href: BIFROST_VERIFY_SPEC, external: true },
    { kind: "Open source", label: "bifrost-verify on GitHub (Apache-2.0)", href: BIFROST_VERIFY_REPO, external: true },
    { kind: "Package", label: "@odingard/bifrost-verify on npm", href: BIFROST_VERIFY_NPM, external: true },
    { kind: "White paper", label: "Bifrost white paper", href: BIFROST_WHITE_PAPER, external: true },
  ] satisfies (CtaLink & { kind: string })[],
  deployment:
    "Evaluations run on synthetic data. Deployment model, data residency and eligibility for regulated data are agreed during onboarding.",
};

export const BIFROST_PATHS = {
  heading: "Engage with Bifrost",
  items: [
    { audience: "Enterprises, software vendors, procurement teams, and trust and security leaders.", title: "Business buyer", cta: { label: "Start free on Bifrost", href: BIFROST_SITE, external: true } },
    { audience: "vCISOs, security consultants, GRC service providers, and technology partners.", title: "Consulting and channel partner", cta: { label: "Become a Bifrost Partner", href: BIFROST_CONTACT.partner } },
    { audience: "Developers, platform architects, and enterprise AI teams.", title: "Technical integration", cta: { label: "Explore Bifrost Integration", href: BIFROST_CONTACT.integration } },
  ],
};
